import type { DesignNode } from '@repo/api-contracts';
import { ChevronDown, ChevronUp, Group, ImageIcon, Type } from 'lucide-react';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';

interface LayerListProps {
  readonly layers: readonly DesignNode[];
  readonly selectedNodeId: string | null;
  /** 저장 오류가 가리킨 레이어 — aria-invalid 와 강조 표시. */
  readonly invalidNodeIds?: ReadonlySet<string>;
  readonly onSelect: (nodeId: string) => void;
  readonly onReorder: (nodeId: string, direction: 'up' | 'down') => void;
}

const NO_INVALID: ReadonlySet<string> = new Set();

const labelOf = (node: DesignNode): string => {
  if (node.type === 'text') return node.text.split('\n')[0] ?? '텍스트';
  return node.type === 'image' ? '이미지' : '그룹';
};

const ICONS = { text: Type, image: ImageIcon, group: Group } as const;

const MOVE =
  'inline-flex size-8 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-25';

/**
 * 현재 컴포넌트의 레이어 — 얇은 선으로 나눈 줄 목록. 고른 줄은 왼쪽 굵은 막대와 굵은 글자로 표시한다.
 * 배열 뒤(위)가 목록 위에 온다(D13).
 */
export const LayerList = ({
  layers,
  selectedNodeId,
  invalidNodeIds = NO_INVALID,
  onSelect,
  onReorder,
}: LayerListProps): ReactElement => {
  if (layers.length === 0)
    return (
      <p className="border-y border-foreground/10 py-5 text-xs leading-relaxed text-muted-foreground">
        아직 넣은 디자인이 없습니다. 스테이지의 [이미지 업로드]·[텍스트]로 시작하세요.
      </p>
    );
  const topIndex = layers.length - 1;
  return (
    <div role="listbox" aria-label="레이어" className="border-t border-foreground">
      {[...layers].reverse().map((node, reversedIndex) => {
        const Icon = ICONS[node.type];
        const index = topIndex - reversedIndex;
        const selected = node.id === selectedNodeId;
        const invalid = invalidNodeIds.has(node.id);
        return (
          <div
            key={node.id}
            role="option"
            aria-selected={selected}
            aria-invalid={invalid ? true : undefined}
            className={cn(
              'relative flex cursor-pointer items-center gap-3 border-b border-foreground/10 py-2 pr-1 pl-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
              'before:absolute before:inset-y-0 before:left-0 before:w-0.5',
              selected
                ? 'bg-muted/70 font-semibold before:bg-foreground'
                : 'hover:bg-muted/40 before:bg-transparent',
              invalid &&
                'text-destructive ring-1 ring-destructive ring-inset before:bg-destructive',
            )}
            onClick={() => onSelect(node.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target === e.currentTarget) onSelect(node.id);
            }}
            tabIndex={0}
          >
            <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            <span className="min-w-0 flex-1 truncate">{labelOf(node)}</span>
            <span className="flex">
              <button
                type="button"
                aria-label="위로"
                title="한 단계 앞으로"
                className={MOVE}
                disabled={index === topIndex}
                onClick={(e) => {
                  e.stopPropagation();
                  onReorder(node.id, 'up');
                }}
              >
                <ChevronUp aria-hidden className="size-4" />
              </button>
              <button
                type="button"
                aria-label="아래로"
                title="한 단계 뒤로"
                className={MOVE}
                disabled={index === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onReorder(node.id, 'down');
                }}
              >
                <ChevronDown aria-hidden className="size-4" />
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
};
